import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Upload,
  FileText,
  X,
  ShieldCheck,
} from "lucide-react";
import { BRAND } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Toaster } from "@/components/ui/sonner";
import Gauge from "@/components/Gauge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const MAX_UPLOAD = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];

const DEBT_OPTIONS = [
  { id: "Zero Debt", title: "Zero Debt", desc: "Fully debt-free with zero ongoing EMIs." },
  { id: "Strategic / Tax-Efficient", title: "Strategic / Tax-Efficient", desc: "Home loan or structured tax-optimised debt only." },
  { id: "Business / Asset Leverage", title: "Business / Asset Leverage", desc: "Commercial loans tied strictly to machinery or business expansion." },
  { id: "Consumption Debt", title: "Consumption Debt", desc: "Personal loans, auto loans or revolving credit card balances." },
];

const PROTECTION_OPTIONS = [
  { id: "Fully Shielded", title: "Fully Shielded", desc: "Adequate standalone pure Term Life + independent Health Cover." },
  { id: "Dependent on Employer / Business", title: "Dependent on Employer / Business", desc: "Relying primarily on corporate group covers." },
  { id: "Linked to Investments", title: "Linked to Investments", desc: "Traditional policies like LIC, ULIPs or endowment plans." },
  { id: "Underinsured / Uncovered", title: "Underinsured / Uncovered", desc: "Minimal or no active insurance policies." },
];

const STRESS_OPTIONS = [
  { id: "Severe Impact", title: "Severe Impact", desc: "A 20% portfolio drop would force me to delay major life goals or liquidate assets." },
  { id: "Moderate Impact", title: "Moderate Impact", desc: "Emotionally uncomfortable, but living standards and near-term commitments remain unaffected." },
  { id: "No Impact", title: "No Impact", desc: "Adequately buffered in cash; market drops are viewed as buying opportunities." },
];

const riskLabel = (score) => {
  if (score <= 2) return "Absolute Preservation. Zero loss tolerance, high liquidity.";
  if (score <= 4) return "Conservative / Income. Heavy safety focus, minor volatility.";
  if (score <= 6) return "Balanced / Moderate. Accepts standard market cycles for steady growth.";
  if (score <= 8) return "Growth-Oriented. Comfortably rides out drawdowns for capital compounding.";
  return "Aggressive Maximisation. High volatility tolerance for maximum long-term returns.";
};

const STEPS = [
  { title: "The Baseline", subtitle: "Personal essentials." },
  { title: "Cash Flow Mechanics", subtitle: "Monthly deployment." },
  { title: "Portfolio Architecture", subtitle: "Current allocation." },
  { title: "Balance Sheet", subtitle: "Leverage & protection." },
  { title: "Risk Capacity", subtitle: "Volatility tolerance." },
  { title: "Strategic Horizon", subtitle: "Primary objective." },
];

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

function computeHealthScore(f) {
  const surplus = Number(f.cash.surplus || 0);
  const liquid = Number(f.portfolio.liquid_buffer || 0);
  const physical = Number(f.portfolio.physical_assets || 0);

  let cf = Math.min(30, surplus * 1.2);

  let liq = Math.min(20, liquid * 2);
  const rePenalty = Math.max(0, (physical - 50) * 0.5);
  liq = liq - rePenalty;

  let debt = 0;
  if (f.debt_profile === "Zero Debt") debt = 25;
  else if (f.debt_profile === "Strategic / Tax-Efficient") debt = 20;
  else if (f.debt_profile === "Business / Asset Leverage") debt = 15;

  let protection = 0;
  if (f.protection_profile === "Fully Shielded") protection = 25;
  else if (f.protection_profile === "Dependent on Employer / Business") protection = 10;

  const raw = cf + liq + debt + protection;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/* ---------- Small building blocks ---------- */
const Field = ({ label, htmlFor, children, hint, error }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="overline text-[0.62rem] text-[#0F1E3D]">
      {label}
    </Label>
    {children}
    {hint && !error && <p className="text-xs font-light text-[#4A5568]">{hint}</p>}
    {error && <p className="text-xs font-medium text-[#B23A48]">{error}</p>}
  </div>
);

const OptionCard = ({ selected, onClick, title, desc, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left rounded-sm border p-5 transition-colors duration-300 ${
      selected
        ? "border-[#C89933] bg-[#C89933]/5"
        : "border-[#E2E8F0] bg-white hover:border-[#C89933]/60"
    }`}
  >
    <div className="flex items-start gap-3">
      <span
        className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
          selected ? "border-[#C89933] bg-[#C89933]" : "border-[#E2E8F0] bg-white"
        }`}
      >
        {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </span>
      <div>
        <p className="font-serif-display text-lg text-[#0F1E3D] tracking-tight">{title}</p>
        <p className="mt-1 text-sm font-light text-[#4A5568] leading-relaxed">{desc}</p>
      </div>
    </div>
  </button>
);

const PercentSlider = ({ label, value, onChange, testid }) => (
  <div className="space-y-3">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
      <Label className="text-sm font-medium text-[#0F1E3D] leading-snug">{label}</Label>
      <span className="font-serif-display text-2xl text-[#0F1E3D] tabular-nums shrink-0" data-testid={`${testid}-value`}>
        {value}
        <span className="text-[#C89933] text-base ml-0.5">%</span>
      </span>
    </div>
    <Slider
      value={[value]}
      onValueChange={(v) => onChange(v[0])}
      min={0}
      max={100}
      step={1}
      data-testid={testid}
    />
  </div>
);

/* ---------- Progress Bar ---------- */
const ProgressBar = ({ current, total }) => {
  const pct = ((current + 1) / total) * 100;
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3">
        <span className="overline text-[0.6rem] text-[#C89933]">
          Step {current + 1} of {total}
        </span>
        <span className="overline text-[0.6rem] text-[#4A5568]">{STEPS[current].title}</span>
      </div>
      <div className="relative h-[3px] w-full bg-[#E2E8F0] overflow-hidden rounded-full">
        <motion.div
          className="absolute inset-y-0 left-0 bg-[#0F1E3D]"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: EASE }}
        />
      </div>
    </div>
  );
};

/* ---------- Header ---------- */
const TopBar = () => (
  <header className="border-b border-[#E2E8F0] bg-[#FAFAF8]/80 backdrop-blur-xl sticky top-0 z-40">
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="discovery-logo">
        <img src={BRAND.logo} alt="JM Investment Advisory" className="h-9 w-auto object-contain" />
        <span className="font-trajan text-[0.75rem] sm:text-sm font-medium uppercase tracking-[0.16em] text-[#0F1E3D]">
          JM Investment Advisory
        </span>
      </Link>
      <Link
        to="/"
        className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#4A5568] hover:text-[#0F1E3D] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Exit
      </Link>
    </div>
  </header>
);

/* ---------- Main Page ---------- */
export default function DiscoveryPage() {
  useEffect(() => {
    document.body.style.background = "#FAFAF8";
    window.scrollTo(0, 0);
  }, []);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { score, ... }

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    industry: "",
    identityFile: null,
    cash: { overheads: 50, discretionary: 20, surplus: 30 },
    portfolio: { equity: 25, fixed_income: 25, physical_assets: 25, liquid_buffer: 25 },
    debt_profile: "",
    protection_profile: "",
    risk_score: 5,
    stress_test: "",
    primary_objective: "",
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateCash = (k, v) => setForm((f) => ({ ...f, cash: { ...f.cash, [k]: v } }));
  const updatePortfolio = (k, v) => setForm((f) => ({ ...f, portfolio: { ...f.portfolio, [k]: v } }));

  const cashTotal = form.cash.overheads + form.cash.discretionary + form.cash.surplus;
  const portfolioTotal =
    form.portfolio.equity +
    form.portfolio.fixed_income +
    form.portfolio.physical_assets +
    form.portfolio.liquid_buffer;

  const step1Errors = useMemo(
    () => ({
      name: form.name.trim().length === 0 ? "Please share your full name." : "",
      email: !emailValid(form.email) ? "Enter a valid email address." : "",
      phone: form.phone.trim().length < 6 ? "Enter a valid mobile number." : "",
      age: !(Number(form.age) > 0) ? "Enter your current age." : "",
      industry: form.industry.trim().length === 0 ? "Share your profession or industry." : "",
    }),
    [form.name, form.email, form.phone, form.age, form.industry]
  );

  const stepInvalidMessage = useMemo(() => {
    switch (step) {
      case 0: {
        return Object.values(step1Errors).find((v) => v) || "";
      }
      case 1:
        return cashTotal === 100 ? "" : `Cash flow must total 100%. You're currently at ${cashTotal}%.`;
      case 2:
        return portfolioTotal === 100 ? "" : `Portfolio must total 100%. You're currently at ${portfolioTotal}%.`;
      case 3:
        if (!form.debt_profile) return "Please select your Debt Profile.";
        if (!form.protection_profile) return "Please select your Protection Profile.";
        return "";
      case 4:
        if (!(form.risk_score >= 1 && form.risk_score <= 10)) return "Set your Risk Capacity from 1 to 10.";
        if (!form.stress_test) return "Please pick a Correction Stress Test option.";
        return "";
      case 5:
        return form.primary_objective.trim().length >= 5 ? "" : "Please describe your primary 3–5 year objective.";
      default:
        return "";
    }
  }, [step, step1Errors, cashTotal, portfolioTotal, form.debt_profile, form.protection_profile, form.risk_score, form.stress_test, form.primary_objective]);

  const stepValid = !stepInvalidMessage;
  const [attempted, setAttempted] = useState(false);

  const next = () => {
    setAttempted(true);
    if (!stepValid) {
      toast.error(stepInvalidMessage);
      return;
    }
    setAttempted(false);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => { setAttempted(false); setStep((s) => Math.max(0, s - 1)); };

  const handleFile = (file) => {
    if (!file) return update({ identityFile: null });
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Only PDF, JPG or PNG files are accepted.");
      return;
    }
    if (file.size > MAX_UPLOAD) {
      toast.error("File exceeds 5 MB.");
      return;
    }
    update({ identityFile: file });
  };

  const handleSubmit = async () => {
    setAttempted(true);
    if (!stepValid) {
      toast.error(stepInvalidMessage);
      return;
    }
    setSubmitting(true);
    try {
      let identity_proof = null;
      if (form.identityFile) {
        const fd = new FormData();
        fd.append("file", form.identityFile);
        const up = await axios.post(`${API}/discovery/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        identity_proof = up.data;
      }

      const client_health_score = computeHealthScore(form);
      const payload = {
        personal_info: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          age: Number(form.age),
          industry: form.industry,
        },
        cash_flow_percent: {
          fixed_overheads: form.cash.overheads,
          discretionary: form.cash.discretionary,
          investable_surplus: form.cash.surplus,
        },
        portfolio_percent: {
          equity: form.portfolio.equity,
          fixed_income: form.portfolio.fixed_income,
          physical_assets: form.portfolio.physical_assets,
          liquid_buffer: form.portfolio.liquid_buffer,
        },
        debt_profile: form.debt_profile,
        protection_profile: form.protection_profile,
        risk_profile: {
          score_1_to_10: form.risk_score,
          stress_test_reaction: form.stress_test,
        },
        primary_objective: form.primary_objective,
        identity_proof,
        health_score: client_health_score,
        submitted_at: new Date().toISOString(),
      };

      const res = await axios.post(`${API}/discovery/submit`, payload);
      const server_score = res?.data?.health_score;
      const final_score = Number.isFinite(server_score) ? server_score : client_health_score;
      setResult({ score: final_score, name: form.name });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong submitting your intake. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <ResultsDashboard score={result.score} name={result.name} />;

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#0F1E3D]" data-testid="discovery-page">
      <TopBar />

      <main className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 pb-24 lg:pt-16">
        <div className="mb-8 lg:mb-10">
          <p className="eyebrow text-[#C89933] mb-3">Client Discovery</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            A structured intake, thoughtfully paced.
          </h1>
        </div>

        <ProgressBar current={step} total={STEPS.length} />

        <div className="mt-8 lg:mt-10 rounded-sm border border-[#E2E8F0] bg-white p-6 sm:p-10 lg:p-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              {step === 0 && (
                <Step1
                  form={form}
                  update={update}
                  onFile={handleFile}
                  errors={attempted ? step1Errors : {}}
                />
              )}
              {step === 1 && (
                <Step2 form={form} updateCash={updateCash} total={cashTotal} />
              )}
              {step === 2 && (
                <Step3 form={form} updatePortfolio={updatePortfolio} total={portfolioTotal} />
              )}
              {step === 3 && <Step4 form={form} update={update} />}
              {step === 4 && <Step5 form={form} update={update} />}
              {step === 5 && <Step6 form={form} update={update} />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] px-6 py-3 text-sm font-medium text-[#0F1E3D] hover:border-[#0F1E3D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="discovery-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              className="group inline-flex items-center gap-3 rounded-full bg-[#0F1E3D] px-8 py-3.5 text-sm font-medium text-[#FAFAF8] hover:bg-[#C89933] hover:text-[#0F1E3D] transition-colors"
              data-testid="discovery-next"
            >
              Continue
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="group inline-flex items-center gap-3 rounded-full bg-[#C89933] px-8 py-3.5 text-sm font-medium text-[#0F1E3D] hover:bg-[#0F1E3D] hover:text-[#FAFAF8] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="discovery-submit"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Calculating...</>
              ) : (
                <>Generate My Health Score <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>
              )}
            </button>
          )}
        </div>
      </main>

      <Toaster position="top-center" richColors />
    </div>
  );
}

/* ---------- Step Components ---------- */
function Step1({ form, update, onFile, errors = {} }) {
  const errClass = (k) =>
    errors[k]
      ? "border-[#B23A48] focus-visible:ring-[#B23A48]"
      : "border-[#E2E8F0] focus-visible:ring-[#C89933]";
  return (
    <>
      <StepHeader
        title="Let's Start with the Basics"
        subtitle="No confidential bank statements required. Strategy always precedes paperwork."
      />
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full Name" htmlFor="name" error={errors.name}>
          <Input id="name" data-testid="input-name" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Dishant Jeswani" className={`h-12 rounded-sm ${errClass("name")}`} />
        </Field>
        <Field label="Work Email" htmlFor="email" error={errors.email}>
          <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="you@company.com" className={`h-12 rounded-sm ${errClass("email")}`} />
        </Field>
        <Field label="Mobile Number" htmlFor="phone" error={errors.phone}>
          <Input id="phone" type="tel" data-testid="input-phone" value={form.phone} onChange={(e) => update({ phone: e.target.value })} placeholder="+91 98765 43210" className={`h-12 rounded-sm ${errClass("phone")}`} />
        </Field>
        <Field label="Current Age" htmlFor="age" error={errors.age}>
          <Input id="age" type="number" min={18} max={99} data-testid="input-age" value={form.age} onChange={(e) => update({ age: e.target.value })} placeholder="35" className={`h-12 rounded-sm ${errClass("age")}`} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Primary Profession / Industry" htmlFor="industry" error={errors.industry}>
            <Input id="industry" data-testid="input-industry" value={form.industry} onChange={(e) => update({ industry: e.target.value })} placeholder="e.g. Corporate Law, Manufacturing, Tech" className={`h-12 rounded-sm ${errClass("industry")}`} />
          </Field>
        </div>
        <div className="sm:col-span-2 space-y-2">
          <Label className="overline text-[0.62rem] text-[#0F1E3D]">
            Optional Identity Proof
          </Label>
          {!form.identityFile ? (
            <label
              htmlFor="identity"
              className="flex items-center justify-center gap-3 rounded-sm border border-dashed border-[#E2E8F0] px-5 py-6 cursor-pointer hover:border-[#C89933] hover:bg-[#C89933]/5 transition-colors"
              data-testid="identity-dropzone"
            >
              <Upload className="h-5 w-5 text-[#7F8D76]" strokeWidth={1.6} />
              <span className="text-sm text-[#4A5568] font-light">
                Upload PAN / Aadhaar to fast-track onboarding later
                <span className="block text-xs text-[#7F8D76]">PDF, JPG or PNG · up to 5 MB</span>
              </span>
              <input
                id="identity"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="input-identity"
              />
            </label>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-sm border border-[#E2E8F0] bg-[#C89933]/5 px-5 py-4" data-testid="identity-selected">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-5 w-5 text-[#C89933]" strokeWidth={1.6} />
                <div className="min-w-0">
                  <p className="text-sm text-[#0F1E3D] font-medium truncate">{form.identityFile.name}</p>
                  <p className="text-xs text-[#4A5568] font-light">{(form.identityFile.size / 1024).toFixed(0)} KB</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onFile(null)}
                className="text-[#4A5568] hover:text-[#B23A48]"
                aria-label="Remove file"
                data-testid="identity-remove"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Step2({ form, updateCash, total }) {
  const ok = total === 100;
  return (
    <>
      <StepHeader
        title="Cash Flow Distribution"
        subtitle="How does your monthly post-tax income typically get deployed? (Must total 100%)"
      />
      <div className="mt-10 space-y-8">
        <PercentSlider label="Fixed Living Overheads · Rent/EMI, groceries, utilities, baseline maintenance" value={form.cash.overheads} onChange={(v) => updateCash("overheads", v)} testid="cash-overheads" />
        <PercentSlider label="Discretionary Lifestyle · Dining, travel, leisure, luxury expenses" value={form.cash.discretionary} onChange={(v) => updateCash("discretionary", v)} testid="cash-discretionary" />
        <PercentSlider label="Investable Surplus · Retained capital, active savings, SIPs / deployments" value={form.cash.surplus} onChange={(v) => updateCash("surplus", v)} testid="cash-surplus" />
      </div>
      <TotalIndicator total={total} ok={ok} testid="cash-total" />
    </>
  );
}

function Step3({ form, updatePortfolio, total }) {
  const ok = total === 100;
  return (
    <>
      <StepHeader
        title="Current Asset Allocation"
        subtitle="Without sharing exact rupee figures, how is your current total net worth distributed? (Must total 100%)"
      />
      <div className="mt-10 space-y-8">
        <PercentSlider label="Equities & Listed Markets · Direct stocks, mutual funds, PMS / AIF" value={form.portfolio.equity} onChange={(v) => updatePortfolio("equity", v)} testid="pf-equity" />
        <PercentSlider label="Fixed Income & Capital Safety · FDs, PPF, EPF, corporate bonds" value={form.portfolio.fixed_income} onChange={(v) => updatePortfolio("fixed_income", v)} testid="pf-fixed" />
        <PercentSlider label="Physical Assets · Commercial real estate, land, non-primary property, gold" value={form.portfolio.physical_assets} onChange={(v) => updatePortfolio("physical_assets", v)} testid="pf-physical" />
        <PercentSlider label="Liquid Buffer · Savings accounts, cash, liquid mutual funds" value={form.portfolio.liquid_buffer} onChange={(v) => updatePortfolio("liquid_buffer", v)} testid="pf-liquid" />
      </div>
      <TotalIndicator total={total} ok={ok} testid="pf-total" />
    </>
  );
}

function Step4({ form, update }) {
  return (
    <>
      <StepHeader
        title="Balance Sheet Resilience"
        subtitle="Understanding your current leverage and risk insulation."
      />
      <div className="mt-8">
        <p className="overline text-[0.6rem] text-[#7F8D76] mb-4">Debt Profile</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DEBT_OPTIONS.map((o) => (
            <OptionCard
              key={o.id}
              selected={form.debt_profile === o.id}
              onClick={() => update({ debt_profile: o.id })}
              title={o.title}
              desc={o.desc}
              testid={`debt-${o.id.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            />
          ))}
        </div>
      </div>
      <div className="mt-10">
        <p className="overline text-[0.6rem] text-[#7F8D76] mb-4">Protection Profile</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PROTECTION_OPTIONS.map((o) => (
            <OptionCard
              key={o.id}
              selected={form.protection_profile === o.id}
              onClick={() => update({ protection_profile: o.id })}
              title={o.title}
              desc={o.desc}
              testid={`protection-${o.id.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function Step5({ form, update }) {
  return (
    <>
      <StepHeader
        title="Risk Capacity & Volatility Tolerance"
        subtitle="Calibrating a portfolio structure that aligns with your financial comfort."
      />
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-sm font-medium text-[#0F1E3D]">Risk Capacity Grade</Label>
          <span className="font-serif-display text-3xl text-[#0F1E3D] tabular-nums" data-testid="risk-score-value">
            {form.risk_score}
            <span className="text-[#C89933] text-base">/10</span>
          </span>
        </div>
        <Slider
          value={[form.risk_score]}
          onValueChange={(v) => update({ risk_score: v[0] })}
          min={1}
          max={10}
          step={1}
          data-testid="risk-score-slider"
        />
        <motion.p
          key={form.risk_score}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="mt-4 text-sm font-light text-[#4A5568] leading-relaxed"
          data-testid="risk-score-label"
        >
          {riskLabel(form.risk_score)}
        </motion.p>
      </div>
      <div className="mt-10">
        <p className="overline text-[0.6rem] text-[#7F8D76] mb-4">Correction Stress Test</p>
        <div className="grid grid-cols-1 gap-3">
          {STRESS_OPTIONS.map((o) => (
            <OptionCard
              key={o.id}
              selected={form.stress_test === o.id}
              onClick={() => update({ stress_test: o.id })}
              title={o.title}
              desc={o.desc}
              testid={`stress-${o.id.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function Step6({ form, update }) {
  return (
    <>
      <StepHeader
        title="Your Primary Strategic Horizon"
        subtitle="What major transition or milestone are you preparing for?"
      />
      <div className="mt-8">
        <Field label="Primary 3–5 Year Objective">
          <Textarea
            value={form.primary_objective}
            onChange={(e) => update({ primary_objective: e.target.value })}
            placeholder="e.g. Ring-fencing family assets from business risk, planning a business expansion, structuring an intergenerational wealth transfer..."
            rows={6}
            className="rounded-sm border-[#E2E8F0] focus-visible:ring-[#C89933] resize-none"
            data-testid="input-objective"
          />
        </Field>
        <div className="mt-6 flex items-start gap-2 text-xs font-light text-[#7F8D76]">
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.6} />
          <p>Your inputs stay strictly confidential, and are used only to shape your first strategy conversation.</p>
        </div>
      </div>
    </>
  );
}

const StepHeader = ({ title, subtitle }) => (
  <div>
    <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-[1.1] tracking-tight text-[#0F1E3D]">
      {title}
    </h2>
    <p className="mt-3 text-[#4A5568] text-base font-light leading-relaxed max-w-2xl">{subtitle}</p>
  </div>
);

const TotalIndicator = ({ total, ok, testid }) => (
  <div
    className={`mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-sm border px-5 py-4 ${
      ok
        ? "border-[#7F8D76]/30 bg-[#7F8D76]/5 text-[#556B4F]"
        : "border-[#C89933]/40 bg-[#C89933]/5 text-[#8A6B1F]"
    }`}
    data-testid={testid}
  >
    <span className="text-sm font-medium">
      {ok ? "Looks good, your allocation totals 100%" : "Please adjust the sliders so the allocation totals exactly 100%"}
    </span>
    <span className="font-serif-display text-2xl tabular-nums shrink-0">
      {total}
      <span className="text-base">%</span>
    </span>
  </div>
);

/* ---------- Results Dashboard ---------- */
function ResultsDashboard({ score, name }) {
  const navigate = useNavigate();

  const tone =
    score < 60 ? "warning" : score <= 80 ? "moderate" : "optimal";

  const message =
    score < 60
      ? "There are a few important gaps in the way your wealth is set up right now. Let's fix them together."
      : score <= 80
      ? "You've got a good base to work with, but there's real room to sharpen your tax and structure."
      : "Your foundation is genuinely strong. From here, our work is about smart compounding and building for the next generation.";

  const CALENDAR_URL = "/#contact"; // links to booking section on home

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#0F1E3D]" data-testid="discovery-results">
      <TopBar />
      <main className="max-w-3xl mx-auto px-5 lg:px-8 pt-14 pb-24 lg:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="eyebrow text-[#C89933] mb-3">Discovery Complete</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            Your Structural Health Score
          </h1>
          <p className="mt-3 text-[#4A5568] font-light">
            {name ? `${name.split(" ")[0]}, ` : ""}here is a first read on your financial architecture.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.25 }}
          className="mt-10 lg:mt-14 rounded-sm border border-[#E2E8F0] bg-white p-8 sm:p-12 flex flex-col items-center text-center"
        >
          <Gauge score={score} tone={tone} data-testid="gauge" />
          <p
            className="mt-8 max-w-xl text-lg lg:text-xl font-serif-display leading-snug tracking-tight text-[#0F1E3D]"
            data-testid="results-message"
          >
            {message}
          </p>
          <a
            href={CALENDAR_URL}
            onClick={(e) => { e.preventDefault(); navigate("/"); setTimeout(() => { document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }, 400); }}
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#0F1E3D] px-8 py-4 text-sm font-medium text-[#FAFAF8] hover:bg-[#C89933] hover:text-[#0F1E3D] transition-colors"
            data-testid="results-cta"
          >
            Schedule Your Strategy Session
            <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>

        <p className="mt-8 text-xs text-[#7F8D76] font-light text-center">
          A copy of your intake has been shared with the JM Investment Advisory team.
        </p>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
